import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { AlertTriangle, AlertCircle, Info, ExternalLink, RefreshCw, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
  danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertCircle },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Info },
};

const SmartAlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/command-center/alerts`);
      const d = await r.json();
      setAlerts(d.alerts || []);
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const byLevel = alerts.reduce((m, a) => { m[a.level] = (m[a.level] || 0) + 1; return m; }, {});

  return (
    <Card className="p-4 border-gray-200 mb-4" data-testid="smart-alerts-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-red-600" /> Smart Alerts
          {alerts.length > 0 && (
            <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full font-bold">{alerts.length}</span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {byLevel.danger > 0 && <span className="text-[10px] font-bold text-red-700">{byLevel.danger} urgent</span>}
          {byLevel.warning > 0 && <span className="text-[10px] font-bold text-amber-700">{byLevel.warning} watch</span>}
          <button onClick={load} className="text-gray-400 hover:text-gray-700" data-testid="alerts-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      {loading && alerts.length === 0 ? (
        <p className="text-xs text-gray-400 py-4 text-center">Scanning for action items…</p>
      ) : alerts.length === 0 ? (
        <div className="text-center py-6" data-testid="alerts-clear">
          <p className="text-2xl mb-1">✨</p>
          <p className="text-sm font-bold text-gray-900">All clear</p>
          <p className="text-[11px] text-gray-500">No contracts waiting · no invoices overdue · all drivers assigned</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {alerts.map((a, i) => {
            const style = LEVEL_STYLES[a.level] || LEVEL_STYLES.info;
            const Icon = style.icon;
            return (
              <div
                key={i}
                className={`${style.bg} border ${style.border} rounded-lg px-3 py-2 flex items-center gap-3`}
                data-testid={`alert-${a.kind}-${i}`}
              >
                <Icon className={`h-4 w-4 ${style.text} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold ${style.text}`}>{a.title}</p>
                  {a.detail && <p className="text-[10px] text-gray-500 truncate">{a.detail}</p>}
                </div>
                {a.link && (
                  <a href={a.link} target="_blank" rel="noopener noreferrer" className={`flex-shrink-0 ${style.text} hover:opacity-70`} title="Open">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default SmartAlertsPanel;
